; (function (window, document) {
    'use strict';

    class MatyModal {
        element = null;
        destroyContentAfterClose = true;
        activators = [];
        closeElements = [];
        onOpenCallbacks = [];
        onBeforeLeaveCallbacks = [];
        onCloseCallbacks = [];

        constructor({ element, activators, destroyContentAfterClose = true }) {
            this.element = element;
            this.activators = activators;
            this.destroyContentAfterClose = destroyContentAfterClose;
            this.init();
        }

        init() {
            this.#initModal();
            this.#initActivators();

            document.dispatchEvent(new Event("matyModals:init"));
        }

        reinitContent() {
            this.#resetModal();
            this.uninitializeModal();
            this.#initModal();

            this.closeElements = this.element.querySelectorAll(".close-modal");
            this.closeElements.forEach(closeElement => {
                const boundClose = this.#handleClose.bind(this);
                closeElement.addEventListener("click", boundClose);

                closeElement.boundClose = boundClose;
            })

            this.initFooter();
        }

        get isModalInitialized() {
            return this.element.classList.contains("-initialized");
        }

        get isModalOpen() {
            return this.element.classList.contains("-active");
        }

        uninitializeModal() {
            this.element.classList.remove("-initialized");
        }

        onOpen(callback, showPreloader = false) {
            if (typeof callback == "function") {
                this.onOpenCallbacks.push({ callback, showPreloader });
            }
        }

        onBeforeLeave(callback, stopClose = false, showPreloader = false) {
            if (typeof callback == "function") {
                this.onBeforeLeaveCallbacks.push({ callback, stopClose, showPreloader });
            }
        }

        onClose(callback) {
            if (typeof callback == "function") {
                this.onCloseCallbacks.push({ callback });
            }
        }

        async open(playOnOpenCallbacks = true) {
            this.element.classList.add("-is-activating");
            document.body.style.overflowY = "hidden";

            await this.#delay(0);

            this.element.classList.add("-active");
            this.element.classList.remove("-is-activating");

            if (playOnOpenCallbacks) {
                for (const cb of this.onOpenCallbacks) {
                    if (cb.showPreloader) {
                        Foundation.libs.common.showPreloader();
                    }

                    await cb.callback(this);

                    this.initFooter();

                    if (cb.showPreloader) {
                        Foundation.libs.common.hidePreloader();
                    }
                }
            }

            this.closeElements = this.element.querySelectorAll(".close-modal");

            this.boundKeyPress = this.#handleKeyPress.bind(this);
            document.body.addEventListener("keyup", this.boundKeyPress);

            this.closeElements.forEach(closeElement => {
                const boundClose = this.#handleClose.bind(this);
                closeElement.addEventListener("click", boundClose);

                closeElement.boundClose = boundClose;
            })
        }

        async close(useCallbacks = true) {
            if (useCallbacks) {
                for (const cb of this.onBeforeLeaveCallbacks) {
                    if (cb.showPreloader) {
                        Foundation.libs.common.showPreloader();
                    }

                    await cb.callback(this);

                    if (cb.showPreloader) {
                        Foundation.libs.common.hidePreloader();
                    }

                    if (cb.stopClose) {
                        return; // Si le callback doit arrêter la fermeture de la modal on return
                    }
                }
            }

            this.#resetModal();
            this.element.classList.add("-is-activating");
            this.element.classList.remove("-active");

            setTimeout(() => {
                this.element.classList.remove("-is-activating");
            }, 500)

            document.body.style.overflowY = "auto";

            for (const cb of this.onCloseCallbacks) {
                await cb.callback(this);
            }

            this.afterClose();  
        }

        afterClose() {
            if (this.destroyContentAfterClose) {
                this.element.querySelector(".maty-modal__wrapper").innerHTML = "";
            }
        }

        initFooter() {
            const footer = this.element.querySelector(".maty-modal__footer");
            const wrapper = this.element.querySelector(".maty-modal__wrapper");
            const isFooterHidden = footer?.classList.contains("hidden") ?? false;

            if (footer && !isFooterHidden) {
                const footerHeight = footer.getBoundingClientRect()?.height ?? 0;
                const wrapperTop = wrapper.getBoundingClientRect()?.top ?? 0;

                let height = 0;

                if (footer.classList.contains("hidden")) {
                    footerHeight = 0;
                }

                if (wrapperTop > 0) {
                    height += wrapperTop;
                }

                if (footerHeight > 0) {
                    height += footerHeight;
                }

                if (height > 0) {
                    // De base je joue sur la hauteur de la section, le footer étant en position sticky ça fonctionne bien
                    // mais sur safari ça fait n'importe-quoi et je dois trouver un fix donc je joue sur le padding
                    //wrapper.style.height = `calc(100% - ${height}px`;
                    wrapper.style.paddingBottom = `${height}px`;
                }
            } else {
                //wrapper.style.height = "100%";
                wrapper.style.paddingBottom = `0px`;
            }
        }

        scrollToTop() {
            const wrapper = this.element.querySelector(".maty-modal__wrapper");
            wrapper.scrollTop = 0;
        }

        #delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        }

        #initModal() {
            if (!this.isModalInitialized) {
                this.element.modal = this;

                this.element.classList.add("-initialized");
            }
        }

        #initActivators() {
            this.activators.forEach((activator) => {
                if (activator != null && !this.#isActivatorInitialized(activator)) {
                    activator.addEventListener("click", this.#handleOpen.bind(this))

                    activator.classList.add("-initialized");
                }
            })
        }

        #resetModal() {
            if (this.isModalInitialized) {
                if (this.boundKeyPress) {
                    document.body.removeEventListener("keyup", this.boundKeyPress);
                    this.boundKeyPress = null;
                }

                this.closeElements.forEach(closeElement => {
                    if (closeElement.boundClose) {
                        closeElement.removeEventListener("click", closeElement.boundClose);
                        closeElement.boundClose = null;
                    }
                });
            }
        }

        #handleOpen() {
            this.open();
        }

        #handleClose() {
            this.close();
        }

        #handleKeyPress(e) {
            if (e.key == "Escape") {
                this.#handleClose();
                document.body.removeEventListener("keyup", this.#handleKeyPress.bind(this));
            }
        }

        #isActivatorInitialized(activator) {
            return activator.classList.contains("-initialized");
        }
    }

    // Librairie Foundation modernisée
    Foundation.libs.matyModals = {
        name: 'matyModals',
        version: '1.0.0',
        modals: [],

        init() {
            this.initModals();
            this.bindEvents();
        },

        initModals() {
            const elements = document.querySelectorAll('.maty-modal');
            this.modals = Array.from(elements).map(element => {
                const activatorsEl = document.querySelectorAll(`.modal-activator[data-modal-id="${element.id}"]`);
                const activators = Array.from(activatorsEl);
                let destroyContentAfterClose = true;

                const destroyContentAttribute = element.getAttribute("data-destroy-content");

                if (destroyContentAttribute != null) {
                    if (destroyContentAttribute == "false") {
                        destroyContentAfterClose = false;
                    }
                }

                new MatyModal({ element, activators, destroyContentAfterClose })
            });
        },

        bindEvents() {
            document.addEventListener("DOMContentLoaded", () => {
                //console.log(`[${this.name}] DOM prêt`);
            });
        },

        reflow() {
            //console.log(`[${this.name}] Reflow called`);
            this.initModals();
        }
    };

    window.addEventListener("DOMContentLoaded", () => {
        Foundation.libs.matyModals.init();
    });

})(window, document);
